import React from "react";
import { useNavigate } from "react-router-dom";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import Button from "../Button";
import classes from "./record.module.scss";

/** A panel's header action ("See all ›", "Manage") or empty-state action. */
export interface PanelAction {
    label: string;
    /** Hover text — what it opens. */
    title: string;
    icon?: string;
    onClick?: () => void;
    to?: string;
}

const PanelActionControl: React.FC<{ action: PanelAction }> = ({ action }) => {
    const navigate = useNavigate();
    // Always a Button: the theme styles `button.crx-btn` only, so an <a> would render as a bare link.
    const handleClick = () => (action.to ? navigate(action.to) : action.onClick?.());
    return (
        <Button type="button" variant="ghost" size="xs" icon={action.icon} title={action.title} onClick={handleClick}>
            {action.label}
        </Button>
    );
};

interface PanelProps {
    /** Anchor id for "Jump to" links and the edit-mode section index. */
    id?: string;
    title: React.ReactNode;
    /** Small count pill after the title. */
    count?: number | string | null;
    /** Muted one-line summary — "4 services · 3 countries". */
    summary?: React.ReactNode;
    /** What the section is for — a gold ⓘ after the title. */
    help?: string;
    /** The header's right-side action, or a custom node. */
    action?: PanelAction | React.ReactNode;
    /** Shows `emptyText` (and `emptyAction`) instead of the children. */
    isEmpty?: boolean;
    /** One quiet line for an empty panel. */
    emptyText?: React.ReactNode;
    emptyAction?: PanelAction;
    /** Body without padding, for lists of RecordRow that bring their own. */
    flush?: boolean;
    children?: React.ReactNode;
    className?: string;
}

const isPanelAction = (value: unknown): value is PanelAction =>
    !!value && typeof value === "object" && !React.isValidElement(value) && "label" in (value as object) && "title" in (value as object);

/** A titled card on a record page. */
const Panel: React.FC<PanelProps> = ({
    id, title, count, summary, help, action, isEmpty, emptyText, emptyAction, flush, children, className,
}) => (
    <section id={id} className={`${classes.panel} ${className ?? ""}`} data-ai-scope>
        <div className={classes.panelHeader}>
            <h2 className={classes.panelTitle}>{title}</h2>
            {help && (
                <OverlayTrigger placement="top" overlay={<Tooltip id={`${id ?? "panel"}-help`}>{help}</Tooltip>}>
                    <i className={`bi bi-info-circle ${classes.panelHelp}`} tabIndex={0} role="img" aria-label={`About ${typeof title === "string" ? title : "this section"}`} />
                </OverlayTrigger>
            )}
            {count !== undefined && count !== null && count !== "" && <span className={classes.panelCount}>{count}</span>}
            {summary && <span className={classes.panelSummary}>{summary}</span>}
            {action && (
                <span className={classes.panelAction}>
                    {isPanelAction(action) ? <PanelActionControl action={action} /> : action}
                </span>
            )}
        </div>
        {isEmpty ? (
            <div className={classes.panelBody}>
                <div className={classes.panelEmpty}>
                    <span>{emptyText ?? "Nothing recorded yet."}</span>
                    {emptyAction && <PanelActionControl action={emptyAction} />}
                </div>
            </div>
        ) : (
            <div className={`${classes.panelBody} ${flush ? classes.panelBodyFlush : ""}`}>{children}</div>
        )}
    </section>
);

export default Panel;
